import React from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { UserContext } from "contexts/UserContext";
import axiosConfig from "restApi/axiosConfig";
import { USER_KEY } from "CONSTANTS";
import { isIos, isStandalone } from "utils/installBanner";
import {
  START_TOUR_EVENT,
  TOUR_PAGES,
  TOUR_VERSION,
  buildSteps,
  guideToOffer,
  hasSeenTour,
  markSeenInThisBrowser,
} from "utils/tour";

// The guides (steps in utils/tour.js). The first time someone reaches a page
// with the main navigation after this shipped:
//  - everyone else goes straight into the member guide;
//  - a team lead is asked which guide they want (the basics, the team lead
//    tools, or neither), since the lead tools are extra - and when they finish
//    one, they are asked whether they want the other too.
// Skippable at any step; both are replayed from the account menu ("How to use
// the app", and for team leads "How to manage the team"). Mounted once in App.js.
// `ready`: the app has finished checking who is signed in (App.js's authChecked),
// so the account's "already seen it" flag is the server's, not a stale cache.
export default ({ ready = true }) => {
  const { user, setUser } = React.useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [run, setRun] = React.useState(false);
  const [kind, setKind] = React.useState("member");
  const [choosing, setChoosing] = React.useState(false);
  // Which guide to offer a team lead after they finish the other one, and the
  // guides already run in this sitting (so the offer is made once).
  const [offer, setOffer] = React.useState(null);
  const [taken, setTaken] = React.useState([]);
  const [stepIndex, setStepIndex] = React.useState(0);

  const onNavPage = TOUR_PAGES.includes(location.pathname);
  const eligible = ready && user.isAuthenticated && Boolean(user.team) && user.id != null;
  const isLeader = Boolean(user.is_team_leader);

  // `chained`: started from the "want the other one too?" offer, so it joins
  // the guides already run rather than beginning a new sitting.
  const start = React.useCallback((which, chained = false) => {
    setChoosing(false);
    setOffer(null);
    setKind(which);
    setTaken((previous) => (chained ? [...previous, which] : [which]));
    setStepIndex(0);
    setRun(true);
  }, []);

  // Remember on the account (and in this browser, as a backup) that the guide
  // has been offered, so it never comes back on its own - on any device.
  const markSeen = () => {
    markSeenInThisBrowser(user.id);
    axiosConfig
      .post("/api/users/tour-seen/", { version: TOUR_VERSION })
      .then((res) => {
        const updated = { ...user, tour_seen_version: res.data.tour_seen_version };
        try {
          localStorage.setItem(USER_KEY, JSON.stringify(updated));
        } catch (e) {
          // Cache only; the server has it.
        }
        setUser(updated);
      })
      .catch(() => {});
  };

  // First run: once, as soon as they're on a page that has the navigation.
  React.useEffect(() => {
    if (!eligible || !onNavPage || run || choosing || hasSeenTour(user)) return;
    markSeen();
    if (isLeader) setChoosing(true);
    else start("member");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, onNavPage, user.id, user.tour_seen_version]);

  // Replay from the account menu. Off a navigation page (an event, a song)
  // the steps would have nothing to point at, so go to one first.
  React.useEffect(() => {
    const replay = (event) => {
      const which = (event.detail && event.detail.kind) || "member";
      if (!eligible || (which === "leader" && !isLeader)) return;
      if (!TOUR_PAGES.includes(window.location.pathname)) history.push("/song-proposals");
      // Let the new page put its navigation on screen first.
      setTimeout(() => start(which), 300);
    };
    window.addEventListener(START_TOUR_EVENT, replay);
    return () => window.removeEventListener(START_TOUR_EVENT, replay);
  }, [eligible, isLeader, history, start]);

  if (!eligible) return null;

  const steps = buildSteps({ ios: isIos(), standalone: isStandalone(), largeScreen, kind, isLeader });

  const handleCallback = ({ action, index, status, type }) => {
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
      setRun(false);
      // Only a guide seen through to the end prompts the offer - quitting
      // partway means they've had enough for now.
      if (status === STATUS.FINISHED) setOffer(guideToOffer({ isLeader, finished: kind, taken }));
      return;
    }
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // A target that isn't on screen (e.g. a hidden navigation) is skipped
      // rather than leaving the tour stuck on it.
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  };

  return (
    <>
      <Dialog open={choosing} onClose={() => setChoosing(false)} aria-labelledby="guide-choice-title">
        <DialogTitle id="guide-choice-title">Welcome to PlanPraise</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're a team lead, so there are two short guides. Pick one now - you can open either one
            later from the account menu.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap" }}>
          <Button onClick={() => setChoosing(false)}>Not now</Button>
          <Button variant="outlined" onClick={() => start("leader")}>
            Team lead tools
          </Button>
          <Button variant="contained" color="secondary" onClick={() => start("member")} autoFocus>
            The basics
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(offer)} onClose={() => setOffer(null)} aria-labelledby="guide-offer-title">
        <DialogTitle id="guide-offer-title">
          {offer === "leader" ? "Want the team lead tools too?" : "Want the basics too?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {offer === "leader"
              ? "There's a separate short guide to what you can do as a team lead."
              : "That's the short guide to the app that everyone sees."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffer(null)}>No thanks</Button>
          <Button variant="contained" color="secondary" onClick={() => start(offer, true)} autoFocus>
            Show me
          </Button>
        </DialogActions>
      </Dialog>
      <Joyride
        run={run}
        steps={steps}
        stepIndex={stepIndex}
        callback={handleCallback}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableOverlayClose
        locale={{ back: "Back", close: "Close", last: "Done", next: "Next", skip: "Skip" }}
        styles={{
          options: {
            zIndex: theme.zIndex.tooltip + 100,
            primaryColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.background.paper,
            textColor: theme.palette.text.primary,
            arrowColor: theme.palette.background.paper,
            overlayColor: "rgba(0, 0, 0, 0.6)",
          },
          tooltip: { borderRadius: 8 },
          buttonBack: { color: theme.palette.text.secondary },
          buttonSkip: { color: theme.palette.text.secondary },
        }}
      />
    </>
  );
};
